import React, { useCallback, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Input,
  Row,
  Spin,
  Table,
  Tabs,
  Tooltip,
  Typography,
  Alert,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { useSelector } from "react-redux";
import { UserState } from "redux-context/user";
import {
  fetchSupplyMetrics,
  fetchDemandMetrics,
  fetchMarketplaceLiquidityMetrics,
  fetchCampaignsTable,
  fetchCreatorsTable,
  fetchInteractionsTable,
} from "services/SauceService";
import {
  Granularity,
  Comparison,
  SupplyResponse,
  DemandResponse,
  LiquidityResponse,
  CampaignsTableRow,
  CreatorsTableRow,
  InteractionsTableRow,
  InteractionsTableRequest,
} from "interfaces/marketplace-dashboard";
import MetricCard from "./components/MetricCard";
import TrendChart from "./components/TrendChart";
import "./MarketplaceDashboard.scss";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

type RangeValue = [Dayjs | null, Dayjs | null] | null;

const GRANULARITY_OPTIONS: { label: string; value: Granularity }[] = [
  { label: "Monthly", value: "monthly" },
  { label: "Weekly", value: "weekly" },
  { label: "Daily", value: "daily" },
];

// const COMPARISON_OPTIONS: { label: string; value: Comparison }[] = [
//   { label: "None", value: "none" },
//   { label: "Week over Week", value: "wow" },
//   { label: "Month over Month", value: "mom" },
//   { label: "Quarter over Quarter", value: "qoq" },
//   { label: "Year over Year", value: "yoy" },
// ];

const formatRate = (v: number | null): string => {
  if (v === null || v === undefined) return "—";
  return `${(v * 100).toFixed(1)}%`;
};

const formatCount = (v: number | null): string => {
  if (v === null || v === undefined) return "—";
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toLocaleString();
};

const formatDurationHours = (v: number | null): string => {
  if (v === null || v === undefined) return "—";
  if (v < 24) return `${v.toFixed(1)} h`;
  const d = Math.floor(v / 24);
  const h = v - d * 24;
  return `${d}d ${h.toFixed(1)}h`;
};

const formatAppsPerCampaign = (v: number | null): string => {
  if (v === null || v === undefined) return "—";
  return v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 });
};

const formatTableDate = (v: string | null | undefined): string => {
  if (!v) return "—";
  return dayjs(v).format("MMM D, YYYY HH:mm");
};


const TRUNCATED_NOTICE =
  "Showing first 1000 rows. Narrow the date range to see all results.";

const campaignsColumns: ColumnsType<CampaignsTableRow> = [
  { title: "Campaign ID", dataIndex: "campaign_id", key: "campaign_id", width: 220 },
  { title: "Campaign", dataIndex: "campaign_name", key: "campaign_name" },
  {
    title: "Created at",
    dataIndex: "created_at",
    key: "created_at",
    render: (v: string) => formatTableDate(v),
    sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    defaultSortOrder: "descend",
  },
  {
    title: "First invite",
    dataIndex: "first_invite_at",
    key: "first_invite_at",
    render: (v: string | null) => formatTableDate(v),
  },
  {
    title: "First apply",
    dataIndex: "first_apply_at",
    key: "first_apply_at",
    render: (v: string | null, row) => (
      <Tooltip title="Date of first application. Duration below shows time from first invite to first apply.">
        <div>
          <div>{formatTableDate(v)}</div>
          {row.invite_to_apply_hours && (
            <div style={{ color: "#8c8c8c", fontSize: 11 }}>{row.invite_to_apply_hours}</div>
          )}
        </div>
      </Tooltip>
    ),
  },
  {
    title: "First hire",
    dataIndex: "first_hire_at",
    key: "first_hire_at",
    render: (v: string | null, row) => (
      <Tooltip title="Date of first hire. Durations show: apply→hire (from first apply to first hire) and invite→hire (full funnel duration).">
        <div>
          <div>{formatTableDate(v)}</div>
          {row.apply_to_hire_hours && (
            <div style={{ color: "#8c8c8c", fontSize: 11 }}>apply→hire {row.apply_to_hire_hours}</div>
          )}
          {row.invite_to_hire_hours && (
            <div style={{ color: "#8c8c8c", fontSize: 11 }}>invite→hire {row.invite_to_hire_hours}</div>
          )}
        </div>
      </Tooltip>
    ),
  },
  {
    title: "Invites",
    dataIndex: "total_invites",
    key: "total_invites",
    sorter: (a, b) => a.total_invites - b.total_invites,
    align: "right",
  },
  {
    title: "Applies",
    dataIndex: "total_applies",
    key: "total_applies",
    sorter: (a, b) => a.total_applies - b.total_applies,
    align: "right",
    render: (v: number, row) => (
      <Tooltip title="Total applications. % below is applications ÷ invites.">
        <div style={{ textAlign: "right" }}>
          <div>{v}</div>
          {row.pct_applied != null && (
            <div style={{ color: "#8c8c8c", fontSize: 11 }}>{row.pct_applied}%</div>
          )}
        </div>
      </Tooltip>
    ),
  },
  {
    title: "Hires",
    dataIndex: "total_hires",
    key: "total_hires",
    sorter: (a, b) => a.total_hires - b.total_hires,
    align: "right",
    render: (v: number, row) => (
      <Tooltip title="Total hires. % below is hires ÷ applications.">
        <div style={{ textAlign: "right" }}>
          <div>{v}</div>
          {row.pct_hired_of_applied != null && (
            <div style={{ color: "#8c8c8c", fontSize: 11 }}>{row.pct_hired_of_applied}%</div>
          )}
        </div>
      </Tooltip>
    ),
  },
];

const creatorsColumns: ColumnsType<CreatorsTableRow> = [
  {
    title: "@username",
    dataIndex: "creator_username",
    key: "creator_username",
    width: 200,
    render: (v: string) => (v ? `@${v}` : "—"),
  },
  { title: "Creator", dataIndex: "creator_name", key: "creator_name" },
  {
    title: "Invited",
    dataIndex: "total_invited",
    key: "total_invited",
    sorter: (a, b) => a.total_invited - b.total_invited,
    align: "right",
    render: (v: number) => v,
  },
  {
    title: "Applied",
    dataIndex: "total_applied",
    key: "total_applied",
    sorter: (a, b) => a.total_applied - b.total_applied,
    defaultSortOrder: "descend",
    align: "right",
    render: (v: number, row) => (
      <Tooltip title="Total applications. % is applies ÷ invites.">
        <div style={{ textAlign: "right" }}>
          <div>{v}</div>
          {row.pct_applied != null && (
            <div style={{ color: "#8c8c8c", fontSize: 11 }}>{row.pct_applied}%</div>
          )}
        </div>
      </Tooltip>
    ),
  },
  {
    title: "Hired",
    dataIndex: "total_hired",
    key: "total_hired",
    sorter: (a, b) => a.total_hired - b.total_hired,
    align: "right",
    render: (v: number, row) => (
      <Tooltip title="Total hires. % is hires ÷ applies.">
        <div style={{ textAlign: "right" }}>
          <div>{v}</div>
          {row.pct_hired_of_applied != null && (
            <div style={{ color: "#8c8c8c", fontSize: 11 }}>{row.pct_hired_of_applied}%</div>
          )}
        </div>
      </Tooltip>
    ),
  },
];

const interactionsColumns: ColumnsType<InteractionsTableRow> = [
  { title: "Campaign ID", dataIndex: "campaign_id", key: "campaign_id", width: 200 },
  { title: "Campaign", dataIndex: "campaign_name", key: "campaign_name" },
  {
    title: "@username",
    dataIndex: "creator_username",
    key: "creator_username",
    width: 180,
    render: (v: string) => (v ? `@${v}` : "—"),
  },
  { title: "Creator", dataIndex: "creator_name", key: "creator_name" },
  {
    title: "Invited at",
    dataIndex: "invited_at",
    key: "invited_at",
    render: (v: string | null) => formatTableDate(v),
  },
  {
    title: "Applied at",
    dataIndex: "applied_at",
    key: "applied_at",
    render: (v: string | null, row) => (
      <Tooltip title="Application date. Duration below shows time from invite to apply (HH:MM).">
        <div>
          <div>{formatTableDate(v)}</div>
          {row.invite_to_apply_hours && (
            <div style={{ color: "#8c8c8c", fontSize: 11 }}>{row.invite_to_apply_hours}</div>
          )}
        </div>
      </Tooltip>
    ),
  },
  {
    title: "Hired at",
    dataIndex: "hired_at",
    key: "hired_at",
    render: (v: string | null, row) => (
      <Tooltip title="Hire date. Durations show apply→hire (from application to hire) and invite→hire (full funnel duration), in HH:MM.">
        <div>
          <div>{formatTableDate(v)}</div>
          {row.apply_to_hire_hours && (
            <div style={{ color: "#8c8c8c", fontSize: 11 }}>apply→hire {row.apply_to_hire_hours}</div>
          )}
          {row.invite_to_hire_hours && (
            <div style={{ color: "#8c8c8c", fontSize: 11 }}>invite→hire {row.invite_to_hire_hours}</div>
          )}
        </div>
      </Tooltip>
    ),
  },
];

const MarketplaceDashboard: React.FC = () => {
  const { loggedUser } = useSelector((state: UserState) => state.user);

  const [dateRange, setDateRange] = useState<RangeValue>([
    dayjs().subtract(30, "day").startOf("day"),
    dayjs().endOf("day"),
  ]);
  const [tableDateRange, setTableDateRange] = useState<RangeValue>([
    dayjs().subtract(30, "day").startOf("day"),
    dayjs().endOf("day"),
  ]);
  const [granularity, setGranularity] = useState<Granularity>("weekly");
  // const [comparison, setComparison] = useState<Comparison>("none");
  const [comparison] = useState<Comparison>("none");

  const [supplyData, setSupplyData] = useState<SupplyResponse | null>(null);
  const [demandData, setDemandData] = useState<DemandResponse | null>(null);
  const [liquidityData, setLiquidityData] = useState<LiquidityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [campaignsRows, setCampaignsRows] = useState<CampaignsTableRow[] | null>(null);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsError, setCampaignsError] = useState<string | null>(null);
  const [campaignsTruncated, setCampaignsTruncated] = useState(false);

  const [creatorsRows, setCreatorsRows] = useState<CreatorsTableRow[] | null>(null);
  const [creatorsLoading, setCreatorsLoading] = useState(false);
  const [creatorsError, setCreatorsError] = useState<string | null>(null);
  const [creatorsTruncated, setCreatorsTruncated] = useState(false);

  const [interactionsRows, setInteractionsRows] = useState<InteractionsTableRow[] | null>(null);
  const [interactionsLoading, setInteractionsLoading] = useState(false);
  const [interactionsError, setInteractionsError] = useState<string | null>(null);
  const [interactionsTruncated, setInteractionsTruncated] = useState(false);
  const [interactionsCampaignId, setInteractionsCampaignId] = useState("");
  const [interactionsCreatorUsername, setInteractionsCreatorUsername] = useState("");

  const fetchData = useCallback(async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return;

    setLoading(true);
    setError(null);

    const params = {
      start: dateRange[0].toISOString(),
      end: dateRange[1].toISOString(),
      granularity,
      comparison,
    };

    try {
      const [supply, demand, liquidity] = await Promise.all([
        fetchSupplyMetrics(params),
        fetchDemandMetrics(params),
        fetchMarketplaceLiquidityMetrics(params),
      ]);
      setSupplyData(supply);
      setDemandData(demand);
      setLiquidityData(liquidity);
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to load dashboard data. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [dateRange, granularity, comparison]);

  const tableParams = useCallback(() => {
    if (!tableDateRange || !tableDateRange[0] || !tableDateRange[1]) return null;
    return {
      start: tableDateRange[0].toISOString(),
      end: tableDateRange[1].toISOString(),
    };
  }, [tableDateRange]);

  const loadCampaigns = useCallback(async () => {
    const params = tableParams();
    if (!params) return;
    setCampaignsLoading(true);
    setCampaignsError(null);
    try {
      const { rows, truncated } = await fetchCampaignsTable(params);
      setCampaignsRows(rows);
      setCampaignsTruncated(truncated);
    } catch (err: any) {
      setCampaignsError(
        err?.response?.data?.error || err?.message || "Failed to load campaigns table."
      );
    } finally {
      setCampaignsLoading(false);
    }
  }, [tableParams]);

  const loadCreators = useCallback(async () => {
    const params = tableParams();
    if (!params) return;
    setCreatorsLoading(true);
    setCreatorsError(null);
    try {
      const { rows, truncated } = await fetchCreatorsTable(params);
      setCreatorsRows(rows);
      setCreatorsTruncated(truncated);
    } catch (err: any) {
      setCreatorsError(
        err?.response?.data?.error || err?.message || "Failed to load creators table."
      );
    } finally {
      setCreatorsLoading(false);
    }
  }, [tableParams]);

  const loadInteractions = useCallback(async () => {
    const campaignId = interactionsCampaignId.trim();
    const creatorUsername = interactionsCreatorUsername.trim();
    if (!campaignId && !creatorUsername) return;
    setInteractionsLoading(true);
    setInteractionsError(null);
    try {
      const params: InteractionsTableRequest = {};
      if (campaignId) params.campaignId = campaignId;
      if (creatorUsername) params.creatorUsername = creatorUsername;
      if (tableDateRange?.[0]) params.start = tableDateRange[0].toISOString();
      if (tableDateRange?.[1]) params.end = tableDateRange[1].toISOString();
      const { rows, truncated } = await fetchInteractionsTable(params);
      setInteractionsRows(rows);
      setInteractionsTruncated(truncated);
    } catch (err: any) {
      setInteractionsError(
        err?.response?.data?.error || err?.message || "Failed to load interactions table."
      );
    } finally {
      setInteractionsLoading(false);
    }
  }, [interactionsCampaignId, interactionsCreatorUsername, tableDateRange]);

  if (!loggedUser?.email?.includes("@mustard.love")) {
    return (
      <div className="marketplace-dashboard">
        <Alert
          type="error"
          message="Access restricted"
          description="This dashboard is only available to internal Mustard users."
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="marketplace-dashboard">
      <Tabs
        defaultActiveKey="dashboard"
        items={[
          {
            key: "dashboard",
            label: "Dashboard",
            children: (
              <>
                {/* Header */}
                <div className="marketplace-dashboard__header">
                  <Title level={3} style={{ margin: 0 }}>
                    Marketplace Analytics
                  </Title>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Supply (Creators) &amp; Demand (Businesses) platform metrics
                  </Text>
                </div>

                {/* Controls */}
                <Card className="marketplace-dashboard__controls" bordered={false}>
                  <Row gutter={[16, 12]} align="middle">
                    <Col>
                      <Text strong style={{ fontSize: 13, marginRight: 8 }}>
                        Date Range
                      </Text>
                      <RangePicker
                        value={dateRange}
                        onChange={(val) => setDateRange(val as RangeValue)}
                        allowClear={false}
                        format="MMM D, YYYY"
                      />
                    </Col>

                    <Col>
                      <Text strong style={{ fontSize: 13, marginRight: 8 }}>
                        Granularity
                      </Text>
                      <div className="marketplace-dashboard__granularity-toggle">
                        {GRANULARITY_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            className={`granularity-btn${granularity === opt.value ? " active" : ""}`}
                            onClick={() => setGranularity(opt.value)}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </Col>

                    <Col>
                      <Button type="primary" onClick={fetchData} loading={loading}>
                        Apply
                      </Button>
                    </Col>
                  </Row>
                </Card>

                {/* Error state */}
                {error && (
                  <Alert
                    type="error"
                    message={error}
                    showIcon
                    closable
                    onClose={() => setError(null)}
                    style={{ marginBottom: 16 }}
                  />
                )}

                {/* Loading state */}
                {loading && (
                  <div className="marketplace-dashboard__loading">
                    <Spin size="large" tip="Loading metrics…" />
                  </div>
                )}

                {/* Content */}
                {!loading && (supplyData || demandData || liquidityData) && (
                  <>
                    {/* Supply Section */}
                    <div className="marketplace-dashboard__section">
                      <div className="marketplace-dashboard__section-header">
                        <Title level={4} style={{ margin: 0 }}>
                          Supply — Creators
                        </Title>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          Creator acquisition, activation, and engagement funnel
                        </Text>
                      </div>

                      {supplyData ? (
                        <>
                          <Row gutter={[16, 16]}>
                            <Col xs={24} sm={8}>
                              <MetricCard
                                title="First-time Apply Count"
                                description="Creators who submitted their first-ever application in the selected period."
                                summary={supplyData.summary.first_time_apply_count}
                                comparison={comparison}
                                formatValue={formatCount}
                              />
                            </Col>
                            <Col xs={24} sm={8}>
                              <MetricCard
                                title="Apply Rate"
                                description="Applications submitted divided by creators invited (via digest emails) in the period. Null when no invites were sent."
                                summary={supplyData.summary.apply_rate}
                                comparison={comparison}
                                formatValue={formatRate}
                              />
                            </Col>
                            <Col xs={24} sm={8}>
                              <MetricCard
                                title="Active Creators"
                                description="Distinct creators who applied at least once between the start and end of the selected range. With a comparison enabled, the baseline uses the same length window shifted back (e.g. WoW, MoM)."
                                summary={supplyData.summary.active_creators}
                                comparison={comparison}
                                formatValue={formatCount}
                              />
                            </Col>
                          </Row>

                          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                            {supplyData.series.first_time_apply_count.length > 0 && (
                              <Col xs={24} md={12}>
                                <Card bordered={false} className="marketplace-dashboard__chart-card">
                                  <TrendChart
                                    title="First-time Apply Count"
                                    series={supplyData.series.first_time_apply_count}
                                    comparison={comparison}
                                    formatValue={formatCount}
                                  />
                                </Card>
                              </Col>
                            )}
                            {supplyData.series.active_creators.length > 0 && (
                              <Col xs={24} md={12}>
                                <Card bordered={false} className="marketplace-dashboard__chart-card">
                                  <TrendChart
                                    title="Active Creators by period"
                                    series={supplyData.series.active_creators}
                                    comparison={comparison}
                                    formatValue={formatCount}
                                  />
                                </Card>
                              </Col>
                            )}
                          </Row>
                        </>
                      ) : (
                        <Alert type="warning" message="No supply data available for this range." showIcon />
                      )}
                    </div>

                    <Divider />

                    {/* Demand Section */}
                    <div className="marketplace-dashboard__section">
                      <div className="marketplace-dashboard__section-header">
                        <Title level={4} style={{ margin: 0 }}>
                          Demand — Businesses
                        </Title>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          Business acquisition and campaign activity funnel
                        </Text>
                      </div>

                      {demandData ? (
                        <>
                          <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12}>
                              <MetricCard
                                title="First-time Campaign Created"
                                description="Businesses that created their very first campaign in the selected period."
                                summary={demandData.summary.first_time_campaign_created}
                                comparison={comparison}
                                formatValue={formatCount}
                              />
                            </Col>
                            <Col xs={24} sm={12}>
                              <MetricCard
                                title="Active Businesses"
                                description="Distinct businesses with an active campaign whose first linked collab overlaps the selected start–end range (collab startDate/endDate from the child collab). With a comparison enabled, the baseline uses the same length window shifted back (e.g. WoW, MoM)."
                                summary={demandData.summary.active_businesses}
                                comparison={comparison}
                                formatValue={formatCount}
                              />
                            </Col>
                          </Row>

                          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                            {demandData.series.first_time_campaign_created.length > 0 && (
                              <Col xs={24} md={12}>
                                <Card bordered={false} className="marketplace-dashboard__chart-card">
                                  <TrendChart
                                    title="First-time Campaign Created"
                                    series={demandData.series.first_time_campaign_created}
                                    comparison={comparison}
                                    formatValue={formatCount}
                                  />
                                </Card>
                              </Col>
                            )}
                            {demandData.series.active_businesses.length > 0 && (
                              <Col xs={24} md={12}>
                                <Card bordered={false} className="marketplace-dashboard__chart-card">
                                  <TrendChart
                                    title="Active Businesses by period"
                                    series={demandData.series.active_businesses}
                                    comparison={comparison}
                                    formatValue={formatCount}
                                  />
                                </Card>
                              </Col>
                            )}
                          </Row>
                        </>
                      ) : (
                        <Alert type="warning" message="No demand data available for this range." showIcon />
                      )}
                    </div>

                    <Divider />

                    <div className="marketplace-dashboard__section">
                      <div className="marketplace-dashboard__section-header">
                        <Title level={4} style={{ margin: 0 }}>
                          Marketplace liquidity / speed
                        </Title>
                      </div>

                      {liquidityData ? (
                        <>
                          <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
                            Campaigns in this period: {formatCount(liquidityData.meta.campaigns_in_period)} created ·{" "}
                            {formatCount(liquidityData.meta.campaigns_with_applications)} with ≥1 application ·{" "}
                            {formatCount(liquidityData.meta.total_applications)} applications total ·{" "}
                            {formatCount(liquidityData.meta.total_hired_creators)} hired bookings
                          </Text>
                          <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                              <div className="marketplace-dashboard__liquidity-pair">
                                <MetricCard
                                  title="Median time: campaign → first application"
                                  description="Median hours between campaign creation and the first creator application (earliest requestedAt on any booking for that campaign)."
                                  summary={liquidityData.summary.median_hours_campaign_to_first_apply}
                                  comparison={comparison}
                                  formatValue={formatDurationHours}
                                />
                                {(liquidityData.series?.median_hours_campaign_to_first_apply?.length ?? 0) > 0 && (
                                  <Card bordered={false} className="marketplace-dashboard__chart-card" style={{ marginTop: 16 }}>
                                    <TrendChart
                                      title="Median time: campaign → first apply"
                                      series={liquidityData.series.median_hours_campaign_to_first_apply}
                                      comparison={comparison}
                                      formatValue={formatDurationHours}
                                    />
                                  </Card>
                                )}
                              </div>
                            </Col>
                            <Col xs={24} md={12}>
                              <div className="marketplace-dashboard__liquidity-pair">
                                <MetricCard
                                  title="Average time: campaign → first application"
                                  description="Mean hours between campaign creation and the first creator application."
                                  summary={liquidityData.summary.mean_hours_campaign_to_first_apply}
                                  comparison={comparison}
                                  formatValue={formatDurationHours}
                                />
                                {(liquidityData.series?.mean_hours_campaign_to_first_apply?.length ?? 0) > 0 && (
                                  <Card bordered={false} className="marketplace-dashboard__chart-card" style={{ marginTop: 16 }}>
                                    <TrendChart
                                      title="Avg time: campaign → first apply"
                                      series={liquidityData.series.mean_hours_campaign_to_first_apply}
                                      comparison={comparison}
                                      formatValue={formatDurationHours}
                                    />
                                  </Card>
                                )}
                              </div>
                            </Col>
                            <Col xs={24} md={12}>
                              <div className="marketplace-dashboard__liquidity-pair">
                                <MetricCard
                                  title="Applications per campaign"
                                  description="Total applications divided by number of campaigns created in the period."
                                  summary={liquidityData.summary.applications_per_campaign}
                                  comparison={comparison}
                                  formatValue={formatAppsPerCampaign}
                                />
                                {(liquidityData.series?.applications_per_campaign?.length ?? 0) > 0 && (
                                  <Card bordered={false} className="marketplace-dashboard__chart-card" style={{ marginTop: 16 }}>
                                    <TrendChart
                                      title="Applications per campaign"
                                      series={liquidityData.series.applications_per_campaign}
                                      comparison={comparison}
                                      formatValue={formatAppsPerCampaign}
                                    />
                                  </Card>
                                )}
                              </div>
                            </Col>
                            <Col xs={24} md={12}>
                              <div className="marketplace-dashboard__liquidity-pair">
                                <MetricCard
                                  title="Median hired creators per campaign"
                                  description="Median count of bookings in Complete or PendingContentReview per campaign (all collabs linked by campaignId)."
                                  summary={liquidityData.summary.median_hired_creators_per_campaign}
                                  comparison={comparison}
                                  formatValue={formatAppsPerCampaign}
                                />
                                {(liquidityData.series?.median_hired_creators_per_campaign?.length ?? 0) > 0 && (
                                  <Card bordered={false} className="marketplace-dashboard__chart-card" style={{ marginTop: 16 }}>
                                    <TrendChart
                                      title="Median hired creators per campaign"
                                      series={liquidityData.series.median_hired_creators_per_campaign}
                                      comparison={comparison}
                                      formatValue={formatAppsPerCampaign}
                                    />
                                  </Card>
                                )}
                              </div>
                            </Col>
                            <Col xs={24} md={12}>
                              <div className="marketplace-dashboard__liquidity-pair">
                                <MetricCard
                                  title="Average hired creators per campaign"
                                  description="Total hired bookings (Complete or PendingContentReview) divided by number of campaigns created in the period."
                                  summary={liquidityData.summary.average_hired_creators_per_campaign}
                                  comparison={comparison}
                                  formatValue={formatAppsPerCampaign}
                                />
                                {(liquidityData.series?.average_hired_creators_per_campaign?.length ?? 0) > 0 && (
                                  <Card bordered={false} className="marketplace-dashboard__chart-card" style={{ marginTop: 16 }}>
                                    <TrendChart
                                      title="Average hired creators per campaign"
                                      series={liquidityData.series.average_hired_creators_per_campaign}
                                      comparison={comparison}
                                      formatValue={formatAppsPerCampaign}
                                    />
                                  </Card>
                                )}
                              </div>
                            </Col>
                            <Col xs={24} md={12}>
                              <div className="marketplace-dashboard__liquidity-pair">
                                <MetricCard
                                  title="Late submission rate"
                                  description="Late submissions divided by total submissions (Complete bookings plus late). Late = WaitingContent or PendingContentReview, no post links, content due (visit date + 5 days) before the snapshot time. Scoped to bookings on collabs linked to campaigns created in the period."
                                  summary={liquidityData.summary.late_submission_rate}
                                  comparison={comparison}
                                  formatValue={formatRate}
                                />
                                {(liquidityData.series?.late_submission_rate?.length ?? 0) > 0 && (
                                  <Card bordered={false} className="marketplace-dashboard__chart-card" style={{ marginTop: 16 }}>
                                    <TrendChart
                                      title="Late submission rate"
                                      series={liquidityData.series.late_submission_rate}
                                      comparison={comparison}
                                      formatValue={formatRate}
                                    />
                                  </Card>
                                )}
                              </div>
                            </Col>
                          </Row>
                        </>
                      ) : (
                        <Alert type="warning" message="No liquidity data available for this range." showIcon />
                      )}
                    </div>
                  </>
                )}

                {/* Empty state */}
                {!loading && !supplyData && !demandData && !liquidityData && !error && (
                  <div className="marketplace-dashboard__empty">
                    <Text type="secondary">
                      Select a date range and click <strong>Apply</strong> to load metrics.
                    </Text>
                  </div>
                )}
              </>
            ),
          },
          {
            key: "data",
            label: "Data by Campaign",
            children: (
              <>
                <Card className="marketplace-dashboard__controls" bordered={false}>
                  <Row gutter={[16, 12]} align="middle">
                    <Col>
                      <Text strong style={{ fontSize: 13, marginRight: 8 }}>
                        Date Range
                      </Text>
                      <RangePicker
                        value={tableDateRange}
                        onChange={(val) => setTableDateRange(val as RangeValue)}
                        allowClear={false}
                        format="MMM D, YYYY"
                      />
                    </Col>
                  </Row>
                </Card>

                <div className="marketplace-dashboard__section">
                  <div className="marketplace-dashboard__section-header">
                    <Title level={4} style={{ margin: 0 }}>
                      Campaigns
                    </Title>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      Per-campaign invite/apply/hire totals (created in the selected date range).
                    </Text>
                  </div>
                  <Button
                    type="primary"
                    onClick={loadCampaigns}
                    loading={campaignsLoading}
                    disabled={!tableDateRange?.[0] || !tableDateRange?.[1]}
                  >
                    Load campaigns
                  </Button>
                  {campaignsError && (
                    <Alert
                      type="error"
                      message={campaignsError}
                      showIcon
                      closable
                      onClose={() => setCampaignsError(null)}
                      style={{ marginTop: 12 }}
                    />
                  )}
                  {campaignsTruncated && (
                    <Alert type="warning" message={TRUNCATED_NOTICE} showIcon style={{ marginTop: 12 }} />
                  )}
                  {campaignsRows && (
                    <Table<CampaignsTableRow>
                      size="small"
                      rowKey="campaign_id"
                      columns={campaignsColumns}
                      dataSource={campaignsRows}
                      pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ["25", "50", "100", "250"] }}
                      scroll={{ x: 1300 }}
                      style={{ marginTop: 12 }}
                    />
                  )}
                </div>

                <Divider />

                <div className="marketplace-dashboard__section">
                  <div className="marketplace-dashboard__section-header">
                    <Title level={4} style={{ margin: 0 }}>
                      Creators
                    </Title>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      Per-creator totals across campaigns created in the selected range.
                    </Text>
                  </div>
                  <Button
                    type="primary"
                    onClick={loadCreators}
                    loading={creatorsLoading}
                    disabled={!tableDateRange?.[0] || !tableDateRange?.[1]}
                  >
                    Load creators
                  </Button>
                  {creatorsError && (
                    <Alert
                      type="error"
                      message={creatorsError}
                      showIcon
                      closable
                      onClose={() => setCreatorsError(null)}
                      style={{ marginTop: 12 }}
                    />
                  )}
                  {creatorsTruncated && (
                    <Alert type="warning" message={TRUNCATED_NOTICE} showIcon style={{ marginTop: 12 }} />
                  )}
                  {creatorsRows && (
                    <Table<CreatorsTableRow>
                      size="small"
                      rowKey="creator_username"
                      columns={creatorsColumns}
                      dataSource={creatorsRows}
                      pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ["25", "50", "100", "250"] }}
                      scroll={{ x: 800 }}
                      style={{ marginTop: 12 }}
                    />
                  )}
                </div>

                <Divider />

                <div className="marketplace-dashboard__section">
                  <div className="marketplace-dashboard__section-header">
                    <Title level={4} style={{ margin: 0 }}>
                      Interactions (Campaign × Creator)
                    </Title>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      One row per (campaign, creator) pair, with invite/apply/hire timestamps.
                    </Text>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                    <Input
                      placeholder="Campaign ID"
                      value={interactionsCampaignId}
                      onChange={(e) => setInteractionsCampaignId(e.target.value)}
                      style={{ width: 240 }}
                      allowClear
                    />
                    <Input
                      placeholder="Creator @username"
                      value={interactionsCreatorUsername}
                      onChange={(e) => setInteractionsCreatorUsername(e.target.value)}
                      style={{ width: 240 }}
                      allowClear
                    />
                  </div>
                  <Button
                    type="primary"
                    onClick={loadInteractions}
                    loading={interactionsLoading}
                    disabled={!interactionsCampaignId.trim() && !interactionsCreatorUsername.trim()}
                  >
                    Load interactions
                  </Button>
                  {interactionsError && (
                    <Alert
                      type="error"
                      message={interactionsError}
                      showIcon
                      closable
                      onClose={() => setInteractionsError(null)}
                      style={{ marginTop: 12 }}
                    />
                  )}
                  {interactionsTruncated && (
                    <Alert type="warning" message={TRUNCATED_NOTICE} showIcon style={{ marginTop: 12 }} />
                  )}
                  {interactionsRows && (
                    <Table<InteractionsTableRow>
                      size="small"
                      rowKey={(row) => `${row.campaign_id}|${row.creator_username}`}
                      columns={interactionsColumns}
                      dataSource={interactionsRows}
                      pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ["25", "50", "100", "250"] }}
                      scroll={{ x: 1300 }}
                      style={{ marginTop: 12 }}
                    />
                  )}
                </div>
              </>
            ),
          },
        ]}
      />
    </div>
  );
};

export default MarketplaceDashboard;
